import type { IconName } from '../components/icons';
import { meets, type Requirement } from './permissions';

export interface NavItem {
  to: string;
  label: string;
  /** Shown on the dashboard tiles. */
  description?: string;
  requires?: Requirement;
  /** Match the path exactly (for index routes). */
  end?: boolean;
  /** Sidebar icon; falls back to a plain dot. */
  icon?: IconName;
}

export interface NavSection {
  id: string;
  title: string;
  items: NavItem[];
}

/** Keep only the items the user may use; drop sections that end up empty. */
export function filterNav(sections: readonly NavSection[], permissions: readonly string[] | undefined): NavSection[] {
  return sections
    .map((s) => ({ ...s, items: s.items.filter((i) => meets(permissions, i.requires)) }))
    .filter((s) => s.items.length > 0);
}

/**
 * Tenant workspace navigation. `requires` mirrors the permission the API checks for the section's
 * main list endpoint; routes.tsx reuses it to guard the route (and its sub-routes).
 */
export const TENANT_NAV: readonly NavSection[] = [
  {
    id: 'overview',
    title: 'Overview',
    items: [{ to: '/', label: 'Dashboard', end: true, icon: 'dashboard' }],
  },
  {
    id: 'sales',
    title: 'Sales',
    items: [
      { to: '/orders', label: 'Orders', icon: 'orders', description: 'Customer orders, approvals, supply and dispatch', requires: { all: ['orders.view'] } },
      { to: '/approvals', label: 'Approvals', icon: 'approvals', description: 'Orders waiting for your decision', requires: { all: ['approvals.view'] } },
      { to: '/customers', label: 'Customers', icon: 'customers', description: 'Customer records, addresses, licences and compliance', requires: { all: ['parties.view'] } },
      { to: '/finance/proformas', label: 'Proforma invoices', icon: 'proformas', description: 'Proformas raised against approved orders', requires: { all: ['finance.view'] } },
    ],
  },
  {
    id: 'procurement',
    title: 'Procurement',
    items: [
      { to: '/purchase-orders', label: 'Purchase orders', icon: 'purchase-orders', description: 'Manufacturer POs, acknowledgements and goods receipt', requires: { all: ['procurement.view'] } },
      { to: '/manufacturers', label: 'Manufacturers', icon: 'manufacturers', description: 'Manufacturer records and compliance', requires: { all: ['parties.view'] } },
    ],
  },
  {
    id: 'inventory',
    title: 'Inventory',
    items: [
      { to: '/products', label: 'Products', icon: 'products', description: 'Catalogue, prices and manufacturer mappings', requires: { all: ['products.view'] } },
      { to: '/inventory', label: 'Batches & FEFO', icon: 'inventory', description: 'Stock by batch and location, movements and adjustments', requires: { all: ['inventory.view'] } },
      { to: '/inventory/expiry', label: 'Expiry risk', icon: 'clock', description: 'Stock approaching or past expiry, by value', requires: { all: ['inventory.view'] } },
    ],
  },
  {
    id: 'logistics',
    title: 'Logistics',
    items: [
      { to: '/logistics/shipments', label: 'Shipments', icon: 'truck', description: 'Consignments, tracking milestones and delivery documents', requires: { all: ['logistics.view'] } },
      { to: '/logistics/exceptions', label: 'Delivery exceptions', icon: 'alert', description: 'Missed, at-risk and stalled deliveries', requires: { all: ['logistics.view'] } },
      { to: '/logistics/partners', label: 'Logistics partners', icon: 'partners', description: 'Carriers, couriers and 3PL providers', requires: { all: ['logistics.view'] } },
      { to: '/logistics/reconciliation', label: 'Freight & reconciliation', icon: 'reconcile', description: 'Carrier charges, recoveries, discrepancies and missing documents', requires: { all: ['logistics.view'] } },
    ],
  },
  {
    id: 'finance',
    title: 'Finance',
    items: [
      { to: '/finance/receivables', label: 'Receivables', icon: 'receivables', description: 'Customer balances by due date and ageing', requires: { all: ['finance.view', 'reports.view'] } },
      { to: '/finance/payables', label: 'Carrier payables', icon: 'freight', description: 'Carrier invoices, approvals and payments', requires: { all: ['logistics.view'] } },
      { to: '/finance/payments', label: 'Payments', icon: 'payments', description: 'Record, confirm and allocate customer payments', requires: { all: ['finance.view'] } },
    ],
  },
  {
    id: 'insights',
    title: 'Insights',
    items: [
      { to: '/reports', label: 'Reports', icon: 'reports', description: 'Sales, delivery performance and freight analysis', requires: { all: ['reports.view'] } },
      { to: '/documents', label: 'Documents', icon: 'documents', description: 'Licences and certificates, expiry and review', requires: { all: ['documents.view'] } },
    ],
  },
  {
    id: 'settings',
    title: 'Administration',
    items: [
      { to: '/settings/company', label: 'Company profile', icon: 'company', description: 'Legal details, logo, identifiers and document footers', requires: { all: ['tenant.settings.manage'] } },
      { to: '/settings/users', label: 'Users', icon: 'users', description: 'Invite people, assign roles, suspend access', requires: { all: ['users.manage'] } },
      { to: '/settings/roles', label: 'Roles', icon: 'roles', description: 'System and custom roles with their permissions', requires: { any: ['users.manage', 'approvals.configure'] } },
      { to: '/settings/approval-policy', label: 'Approval policy', icon: 'policy', description: 'Order approval stages and thresholds', requires: { any: ['approvals.configure', 'approvals.view'] } },
      { to: '/settings/numbering', label: 'Numbering', icon: 'numbering', description: 'Document number prefixes and reset rules', requires: { all: ['tenant.settings.manage'] } },
      { to: '/settings/payment-terms', label: 'Payment terms', icon: 'terms', description: 'Advance, credit and due-day rules', requires: { any: ['credit.manage', 'finance.view'] } },
      { to: '/settings/alerts', label: 'Alert windows', icon: 'bell', description: 'When payments, deliveries and expiry count as due soon or at risk', requires: { all: ['tenant.settings.manage'] } },
      { to: '/settings/support-access', label: 'Support access', icon: 'support', description: 'Time-limited read-only access for platform support', requires: { all: ['users.manage'] } },
      { to: '/settings/audit', label: 'Audit log', icon: 'audit', description: 'Who changed what, and when', requires: { all: ['audit.view'] } },
      { to: '/settings/subscription', label: 'Subscription & usage', icon: 'subscription', description: 'Plan limits and current usage', requires: { all: ['subscription.view'] } },
    ],
  },
  {
    id: 'account',
    title: 'My account',
    items: [{ to: '/settings/sessions', label: 'My sessions', icon: 'sessions', description: 'Devices signed in to your account' }],
  },
];

/** The nav item (and its section) that owns a path: the longest matching prefix wins. */
export function findNavItem(sections: readonly NavSection[], pathname: string): { section: NavSection; item: NavItem } | null {
  let best: { section: NavSection; item: NavItem } | null = null;
  for (const section of sections) {
    for (const item of section.items) {
      const match = item.end ? pathname === item.to : pathname === item.to || pathname.startsWith(`${item.to}/`);
      if (match && (!best || item.to.length > best.item.to.length)) best = { section, item };
    }
  }
  return best;
}

export const PLATFORM_NAV: readonly NavSection[] = [
  {
    id: 'platform',
    title: 'Platform',
    items: [
      { to: '/admin/tenants', label: 'Tenants', icon: 'tenants', requires: { all: ['platform.tenants.view'] } },
      { to: '/admin/plans', label: 'Plans', icon: 'plans', requires: { all: ['platform.tenants.view'] } },
      { to: '/admin/countries', label: 'Countries', icon: 'globe', requires: { all: ['platform.tenants.view'] } },
      { to: '/admin/usage', label: 'Usage', icon: 'subscription', requires: { all: ['platform.usage.view'] } },
      { to: '/admin/audit', label: 'Audit log', icon: 'audit', requires: { all: ['platform.tenants.view'] } },
    ],
  },
];
